/**
 * affiliate-link-enricher.js
 *
 * Per-affiliate-system logic for auto-enriching a raw affiliate link
 * (e.g. one pasted in from an affiliate platform) into a fully-formed
 * tracking link, and for extracting the internalNote from that link.
 *
 * Each system exports two things via its entry in ENRICHERS:
 *   enrich(link, context)  → { affiliateLink, internalNote } | null
 *   extract(link)          → internalNote string | null
 *
 * context = { advertiserName, userName, userCompanyName, userType }
 */

/**
 * Returns the first 3 alphabetic characters of a string, lowercased.
 * e.g. "Let's Jackpot" → "let", "TRAFFICLATE LTD" → "tra"
 */
function first3Alpha(str = '') {
    return str.replace(/[^a-zA-Z]/g, '').slice(0, 3).toLowerCase();
}

/**
 * Resolves the publisher display name from context, respecting userType.
 */
function resolvePublisherName({ userType, userCompanyName, userName } = {}) {
    return userType === 'Company' ? userCompanyName : userName;
}

/**
 * Normalizes a market/geo code into a compact key suffix so the same
 * advertiser+publisher can hold a distinct Cellxpert key per geo.
 * e.g. "IT" -> "it", "GCC" -> "gcc".
 */
function geoSuffix(market = '') {
    return String(market).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

/**
 * MyAffiliates media item — the numeric path segment of a record.* tracking URL.
 * e.g. https://record.primewebaffiliates.com/_CAqvy.../13/?pg=4  ->  "13"
 * The long "_<hash>" segment is never purely numeric, so the numeric segment is
 * unambiguous. Mirrors the server-side detector in helpers/test-link-detect.js.
 */
function myAffiliatesMediaItem(link) {
    try {
        const segs = new URL(link).pathname.split('/').filter(Boolean);
        return segs.reverse().find(seg => /^\d+$/.test(seg)) || null;
    } catch {
        return null;
    }
}

/**
 * Auto-generated campaign key: first3(advertiser) + first3(publisher) + geo.
 * The geo keeps multi-geo links for the SAME advertiser+publisher distinct
 * (e.g. atenauit / atenaude). Returns null when the context lacks what it needs, so a hand-typed key is never
 * replaced with nothing.
 */
function autoKey(context = {}) {
    const publisherName = resolvePublisherName(context);
    if (!context.advertiserName || !publisherName) return null;
    return first3Alpha(context.advertiserName) + first3Alpha(publisherName) + geoSuffix(context.market);
}

function withLiteralBraces(url) {
    return url.toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');
}

const ENRICHERS = {
    /**
     * MyAffiliates
     * Raw:      https://record.primewebaffiliates.com/_CAqvyFAnunsWqcfzuvZcQGNd7ZgqdRLk/13/?pg=4
     * Enriched: https://record.primewebaffiliates.com/_CAqvyFAnunsWqcfzuvZcQGNd7ZgqdRLk/13/?pg=4&s2s.req_id={clickid}
     * internalNote: the media item (the numeric path segment, e.g. "13")
     *
     * The click id is `s2s.req_id` and the campaign key the postback layer routes
     * on is the media item. If the link carries no numeric segment we still append
     * the click-id param but return internalNote: null, so a hand-typed note isn't
     * replaced with nothing.
     */
    myaffiliates: {
        extract(link) {
            return myAffiliatesMediaItem(link);
        },

        enrich(link) {
            try {
                const url = new URL(link);

                if (!url.searchParams.get('s2s.req_id')) {
                    url.searchParams.set('s2s.req_id', '{clickid}');
                }

                const enriched = url.toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');

                return {
                    affiliateLink: enriched,
                    internalNote: myAffiliatesMediaItem(link),
                };
            } catch {
                return null;
            }
        },
    },

    /**
     * Referon
     * Raw:      https://sgc.monalvor.com/?mid=251962_2116744
     * Enriched: https://sgc.monalvor.com/?mid=251962_2116744&pubid=2116744&clickid={clickid}
     * internalNote: the suffix after the underscore in `mid`
     */
    referon: {
        extract(link) {
            try {
                const url = new URL(link);
                const mid = url.searchParams.get('mid');
                if (!mid || !mid.includes('_')) return null;
                const suffix = mid.split('_')[1];
                return suffix || null;
            } catch {
                return null;
            }
        },

        enrich(link) {
            try {
                const url = new URL(link);
                const mid = url.searchParams.get('mid');
                if (!mid || !mid.includes('_')) return null;

                const suffix = mid.split('_')[1];
                if (!suffix) return null;

                if (!url.searchParams.get('pubid')) {
                    url.searchParams.set('pubid', suffix);
                }
                if (!url.searchParams.get('clickid')) {
                    url.searchParams.set('clickid', '{clickid}');
                }

                // Decode %7B / %7D back to literal braces for {clickid}
                const enriched = url.toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');

                return {
                    affiliateLink: enriched,
                    internalNote: suffix,
                };
            } catch {
                return null;
            }
        },
    },

    /**
     * CellXpert
     * Raw:      https://go.afftrackio.com/visit/?bta=35375&nci=5376
     * Enriched: https://go.afftrackio.com/visit/?bta=35375&nci=5376&utm_campaign=lettra&afp1={clickid}
     * internalNote: utm_campaign value (first3(advertiser) + first3(publisher))
     *
     * Requires context: { advertiserName, userName, userCompanyName, userType }
     */
    cellxpert: {
        extract(link) {
            try {
                const url = new URL(link);
                return url.searchParams.get('utm_campaign') || null;
            } catch {
                return null;
            }
        },

        enrich(link, context = {}) {
            try {
                const url = new URL(link);

                let utmCampaign = url.searchParams.get('utm_campaign');

                if (!utmCampaign) {
                    utmCampaign = autoKey(context);
                    if (!utmCampaign) return null;
                    url.searchParams.set('utm_campaign', utmCampaign);
                }

                if (!url.searchParams.get('afp1')) {
                    url.searchParams.set('afp1', '{clickid}');
                }

                const enriched = url.toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');

                return {
                    affiliateLink: enriched,
                    internalNote: utmCampaign,
                };
            } catch {
                return null;
            }
        },
    },

    /**
     * Softswiss
     * Raw:      https://superspins777.net/dpzzmzdkl
     * Enriched: https://superspins777.net/dpzzmzdkl?visit_id={clickid}
     *
     * The URL carries a promo code as its last path segment but NOT the
     * numeric campaign ID that the postback layer keys on — that has to
     * be looked up on the operator's /partner/promos dashboard and typed
     * into Internal Note by hand. So enrich() only appends the click-id
     * param; it returns internalNote: null so the admin's manual entry
     * isn't clobbered.
     */
    softswiss: {
        extract() {
            // The URL doesn't carry the numeric campaign ID; we can't derive
            // internalNote from the link alone. Return null so callers know
            // to leave the field for manual entry.
            return null;
        },

        enrich(link) {
            try {
                const url = new URL(link);
                if (!url.searchParams.get('visit_id')) {
                    url.searchParams.set('visit_id', '{clickid}');
                }
                const enriched = url.toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');
                return {
                    affiliateLink: enriched,
                    internalNote: null,
                };
            } catch {
                return null;
            }
        },
    },

    /**
     * Affise
     * Raw:      https://trk.grdnprtnrs.com/click?pid=60&offer_id=135&l=1793374326
     * Enriched: https://trk.grdnprtnrs.com/click?pid=60&offer_id=135&l=1793374326&sub2=dienaugb&sub1={clickid}
     * internalNote: the sub2 value (first3(advertiser) + first3(publisher) + geo)
     *
     * A link that already carries sub2 keeps it and returns internalNote: null, since
     * older Affise links use sub2 for something else (e.g. a traffic label).
     */
    affise: {
        extract(link) {
            try {
                return new URL(link).searchParams.get('sub2') || null;
            } catch {
                return null;
            }
        },

        enrich(link, context = {}) {
            try {
                const url = new URL(link);

                let note = null;
                if (!url.searchParams.get('sub2')) {
                    note = autoKey(context);
                    if (!note) return null;
                    url.searchParams.set('sub2', note);
                }
                if (!url.searchParams.get('sub1')) {
                    url.searchParams.set('sub1', '{clickid}');
                }

                return { affiliateLink: withLiteralBraces(url), internalNote: note };
            } catch {
                return null;
            }
        },
    },

    /**
     * Netrefer (both "Netrefer" and "Netrefer API"). The shape is read from the URL.
     *   subid present: https://mo.zenlaneo.com/redirect.aspx?pid=422144&lpid=2397&bid=2159&subid={subid}
     *                  becomes ...&subid=<key>-{clickid}  (the key rides in front of the click id)
     *   otherwise:     https://media.betninjapartners.com/redirect.aspx?pid=4122&lpid=16&bid=1488
     *                  becomes ...&campaign=<key>&clickid={clickid}
     * internalNote: the key already on the link, else first3(advertiser) + first3(publisher) + geo
     */
    netrefer: {
        extract(link) {
            try {
                const url = new URL(link);
                const campaign = url.searchParams.get('campaign');
                if (campaign) return campaign;
                const subid = url.searchParams.get('subid');
                if (subid && !/^\{.*\}$/.test(subid)) {
                    return subid.replace(/-\{clickid\}$/, '') || null;
                }
                return null;
            } catch {
                return null;
            }
        },

        enrich(link, context = {}) {
            try {
                const url = new URL(link);

                if (url.searchParams.has('subid')) {
                    const raw = url.searchParams.get('subid');
                    const isPlaceholder = !raw || /^\{.*\}$/.test(raw);
                    const note = isPlaceholder ? autoKey(context) : raw.replace(/-\{clickid\}$/, '');
                    if (!note) return null;
                    url.searchParams.set('subid', `${note}-{clickid}`);
                    return { affiliateLink: withLiteralBraces(url), internalNote: note };
                }

                let note = url.searchParams.get('campaign');
                if (!note) {
                    note = autoKey(context);
                    if (!note) return null;
                    url.searchParams.set('campaign', note);
                }
                if (!url.searchParams.get('clickid')) {
                    url.searchParams.set('clickid', '{clickid}');
                }

                return { affiliateLink: withLiteralBraces(url), internalNote: note };
            } catch {
                return null;
            }
        },
    },
};

const SYSTEM_ALIASES = {
    'netrefer api': 'netrefer',
};

function findEnricher(system) {
    const key = String(system).trim().toLowerCase();
    return ENRICHERS[SYSTEM_ALIASES[key] || key];
}

/**
 * Attempt to enrich a raw affiliate link for the given system.
 * Returns { affiliateLink, internalNote } on success, or null if
 * the link isn't parseable / the system has no enricher.
 *
 * @param {string} link
 * @param {string} system   — affiliateSystem name (case-insensitive)
 * @param {object} context  — { advertiserName, userName, userCompanyName, userType }
 * @returns {{ affiliateLink: string, internalNote: string } | null}
 */
export function enrichAffiliateLink(link, system, context = {}) {
    if (!link || !system) return null;
    const enricher = findEnricher(system);
    if (!enricher) return null;
    return enricher.enrich(link, context);
}

/**
 * Extract the internalNote from a link for the given system,
 * without modifying the link itself.
 *
 * @param {string} link
 * @param {string} system
 * @returns {string | null}
 */
export function extractInternalNote(link, system) {
    if (!link || !system) return null;
    const enricher = findEnricher(system);
    if (!enricher) return null;
    return enricher.extract(link);
}
